import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { useSelector } from "react-redux";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid,
    InputLabel, Select, TextField, Typography } from '@mui/material';
import { Search as IconSearch, DoneAll as IconDoneAll, Undo as IconUndo } from "@mui/icons-material";

import comprobantePagoService, { identificadorPago } from "../../services/comprobantePagoService";
import * as catalogoService from "../../services/catalogoService";
import DataGridBase from "../../components/common/DataGridBase";
import DialogoCredenciales from "../../components/DialogoCredenciales";
import { useMantenimiento } from '../../hooks/useMantenimiento';

// Consolidacion de pagos del comprobante. Se muestran por defecto los pagos
// del dia; se pueden marcar con la casilla de cada fila o con la casilla de la
// cabecera, que marca todos o ninguno, y consolidar los marcados. Solo los pagos
// activos se pueden marcar.
//
// Los pagos ya consolidados se pueden desconsolidar uno por uno desde la columna
// Acciones: es una accion sensible, pide el motivo y, si la seguridad esta
// activa con credenciales, tambien la clave del usuario.

// Catalogo 1 = Estados del pago del comprobante.
const ESTADO_PAGO_ACTIVO = 2;
const ESTADO_PAGO_CONSOLIDADO = 227;

// Catalogo 249 = Motivos de desconsolidacion.
const ID_CATALOGO_MOTIVOS_DESCONSOLIDACION = 249;

const today = () => moment().format('yyyy-MM-DD');

const ConsolidarPagos = (props) => {
    const { isReadOnly } = useMantenimiento("comprobante");

    const accesos = useSelector((state) => state.seguridad?.accesos) || [];

    const [cargando, setCargando] = useState(true);
    const [results, setResults] = useState([]);
    const [seleccion, setSeleccion] = useState([]);
    const [filters, setFilters] = useState({
        idEstado: ESTADO_PAGO_ACTIVO,
        fechaInicio: today(),
        fechaFin: today(),
    });

    // Desconsolidacion.
    const [pagoDesconsolidar, setPagoDesconsolidar] = useState(null);
    const [idMotivoDesconsolidacion, setIdMotivoDesconsolidacion] = useState(0);
    const [listMotivos, setListMotivos] = useState([]);
    const [pidiendoCredenciales, setPidiendoCredenciales] = useState(false);
    const [mensaje, setMensaje] = useState(null);

    const fetchResults = useCallback(async () => {
        setCargando(true);
        setSeleccion([]);

        // Las fechas van en hora local: con toISOString se correrian un dia.
        const fechaInicio = filters.fechaInicio
            ? moment(filters.fechaInicio).startOf("day").format("YYYY-MM-DD HH:mm:ss")
            : null;
        const fechaFin = filters.fechaFin
            ? moment(filters.fechaFin).endOf("day").format("YYYY-MM-DD HH:mm:ss")
            : null;

        const data = await comprobantePagoService.getsComprobantePagoSearch({
            idEstado: filters.idEstado == null ? 0 : filters.idEstado,
            fechaInicio: fechaInicio,
            fechaFin: fechaFin,
            serie: "",
            numero: 0,
            pagador: "",
        }).catch((err) => { console.log("Err: ", err) });

        setResults((data || []).map((row) => ({ ...row, id: row.idComprobante + "-" + row.numero })));
        setCargando(false);
    }, [filters])

    useEffect(() => {
        fetchResults();
    }, [fetchResults])

    const fetchMotivos = useCallback(async () => {
        const data = await catalogoService.getCatalogosByPadre(ID_CATALOGO_MOTIVOS_DESCONSOLIDACION)
            .catch((err) => { console.log("Err: ", err) });

        setListMotivos(data || []);
    }, [])

    useEffect(() => {
        fetchMotivos();
    }, [fetchMotivos])

    // Con la seguridad activa el acceso se comprueba tambien en la API.
    const puedeDesconsolidar = () => {
        if (isReadOnly) return false;
        if (accesos.length === 0) return true;

        return accesos.includes("pago.desconsolidar");
    }

    const desconsolidar = async (token = "", usuarioAprobador = "") => {
        const pagos = [{
            idComprobante: pagoDesconsolidar.idComprobante,
            numero: pagoDesconsolidar.numero,
        }];

        await comprobantePagoService.desconsolidarComprobantePagos(pagos, idMotivoDesconsolidacion, token)
            .then((desconsolido) => {
                setResults((prev) => prev.map((row) => {
                    if (row.id !== pagoDesconsolidar.id) return row;

                    return { ...row, idEstado: ESTADO_PAGO_ACTIVO, estado: "Activo" };
                }));
                setMensaje(desconsolido
                    ? { severity: "success", texto: "El pago quedo activo nuevamente. Lo aprobo " + (usuarioAprobador || "") + "." }
                    : { severity: "warning", texto: "El pago no se pudo desconsolidar." });
                cerrarDesconsolidar();
            })
            .catch((err) => {
                console.log("Err: ", err);
                setMensaje({ severity: "error", texto: "No se pudo desconsolidar el pago." });
            });
    }

    const cerrarDesconsolidar = () => {
        setPagoDesconsolidar(null);
        setIdMotivoDesconsolidacion(0);
        setPidiendoCredenciales(false);
    }

    // La desconsolidacion siempre se aprueba con el usuario y la clave de quien
    // tiene el acceso pago.desconsolidar: el usuario se escribe en blanco.
    const confirmarDesconsolidar = () => {
        setPidiendoCredenciales(true);
    }

    // Solo se pueden marcar los pagos activos: la casilla de la cabecera
    // selecciona solo esos.
    const esSeleccionable = (row) => row.idEstado == ESTADO_PAGO_ACTIVO;

    const consolidar = async () => {
        if (seleccion.length === 0) return;

        const pagos = seleccion.map((id) => {
            const partes = String(id).split("-");

            return { idComprobante: parseInt(partes[0]), numero: parseInt(partes[1]) };
        });

        await comprobantePagoService.consolidarComprobantePagos(pagos)
            .then((consolidados) => {
                setResults((prev) => prev.map((row) => {
                    if (!seleccion.includes(row.id)) return row;

                    return { ...row, idEstado: ESTADO_PAGO_CONSOLIDADO, estado: "Pago Consolidado" };
                }));
                setSeleccion([]);
            })
            .catch((err) => { console.log("Err: ", err) });
    }

    const columns = [
        { field: 'serieNumero', headerName: 'Comprobante', width: 130, sortable: false, headerAlign: 'center'
            , renderCell: (cellValues) => cellValues.row.serieNumero
        },
        { field: 'pagador', headerName: 'Pagador', width: 160, sortable: false, headerAlign: 'left' },
        { field: 'medioPago', headerName: 'Medio de Pago', width: 150, sortable: false, headerAlign: 'left' },
        { field: 'entidadPago', headerName: 'Entidad', width: 110, sortable: false, headerAlign: 'left' },
        { field: 'numeroOperacion', headerName: 'N. Operacion', width: 110, sortable: false, headerAlign: 'left' },
        { field: 'fechaPago', headerName: 'Fecha Pago', width: 110, type: 'date'
            , valueFormatter: (value) => moment(value).format('D/MM/YYYY')
            , sortable: false, headerAlign: 'center', align: 'center' },
        { field: 'monto', headerName: 'Monto', width: 90, sortable: false, headerAlign: 'right', align: 'right'
            , valueFormatter: (value) => (parseFloat(value) || 0).toFixed(2) },
        { field: 'estado', headerName: 'Estado', width: 110, sortable: false, headerAlign: 'center', align: 'center' },
        // La desconsolidacion es una accion sensible: pide motivo y, si la
        // seguridad exige credenciales, tambien la clave del usuario.
        { field: 'desconsolidar', headerName: 'Acciones', width: 130, sortable: false, headerAlign: 'center', align: 'center'
            , renderCell: (cellValues) => {
                const row = cellValues.row;

                if (row.idEstado != ESTADO_PAGO_CONSOLIDADO) return null;
                if (!puedeDesconsolidar()) return null;

                return <Box
                    sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => {
                        setPagoDesconsolidar(row);
                        setIdMotivoDesconsolidacion(0);
                        setMensaje(null);
                    }}
                >
                    Desconsolidar
                </Box>;
            }
        },
    ];

    return (
        <div>
            <Grid container>
                <Grid item>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Consolidar Pagos
                    </Typography>
                </Grid>
                <Grid container spacing={1} padding={"12px 0px 0px 0px"}>
                    <Grid item xs={12} sm={12}>
                        <Grid container item xs={12} sm={12} spacing={1} alignItems="center">
                            <Grid item xs={6} sm={2}>
                                <TextField
                                    name="fechaInicio"
                                    label="Fecha Desde"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    value={filters.fechaInicio}
                                    onChange={(event) => setFilters({ ...filters, fechaInicio: event.target.value })}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <TextField
                                    name="fechaFin"
                                    label="Fecha Hasta"
                                    type="date"
                                    InputLabelProps={{ shrink: true }}
                                    fullWidth
                                    value={filters.fechaFin}
                                    onChange={(event) => setFilters({ ...filters, fechaFin: event.target.value })}
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={6} sm={2}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="input-estadopago">Estado del Pago</InputLabel>
                                    <Select
                                        native
                                        label="Estado del Pago"
                                        value={filters.idEstado}
                                        onChange={(event) => setFilters({ ...filters, idEstado: parseInt(event.target.value) })}
                                        inputProps={{ name: 'idEstado', id: 'input-estadopago' }}
                                        size="small"
                                    >
                                        <option key={ESTADO_PAGO_ACTIVO} value={ESTADO_PAGO_ACTIVO}>{"Activo"}</option>
                                        <option key={ESTADO_PAGO_CONSOLIDADO} value={ESTADO_PAGO_CONSOLIDADO}>{"Pago Consolidado"}</option>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={2} sm={1} display={"flex"} alignItems={"center"}>
                                <Button variant="outlined" size="medium" fullWidth onClick={() => { fetchResults() }}>
                                    <IconSearch />
                                </Button>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr' }}>
                            <DataGridBase
                                rows={results}
                                columns={columns}
                                loading={cargando}
                                disabled={isReadOnly}
                                checkboxSelection
                                disableRowSelectionOnClick
                                isRowSelectable={(params) => esSeleccionable(params.row)}
                                rowSelectionModel={seleccion}
                                onRowSelectionModelChange={(model) => setSeleccion(model)}
                                toolbarButtons={[
                                    {
                                        text: "Consolidar",
                                        icon: <IconDoneAll />,
                                        disabled: seleccion.length === 0,
                                        onClick: consolidar,
                                    }
                                ]}
                            />
                        </Box>
                    </Grid>
                    {mensaje && (
                        <Grid item>
                            <Alert severity={mensaje.severity} onClose={() => setMensaje(null)}>
                                {mensaje.texto}
                            </Alert>
                        </Grid>
                    )}
                </Grid>
            </Grid>

            {/* Motivo de la desconsolidacion. */}
            <Dialog open={pagoDesconsolidar != null} fullWidth maxWidth={"xs"} onClose={cerrarDesconsolidar}>
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Desconsolidar Pago
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={1} alignItems={"center"} sx={{ paddingTop: "12px" }}>
                        <Grid item xs={12} sm={12}>
                            <TextField
                                disabled
                                name="pagoComprobante"
                                label="Pago"
                                fullWidth
                                value={identificadorPago(pagoDesconsolidar)}
                                size="small"
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <FormControl fullWidth required>
                                <InputLabel htmlFor="input-motivodesconsolidacion">Motivo</InputLabel>
                                <Select
                                    native
                                    label="Motivo"
                                    value={idMotivoDesconsolidacion}
                                    onChange={(event) => setIdMotivoDesconsolidacion(parseInt(event.target.value))}
                                    inputProps={{ name: 'idMotivoDesconsolidacion', id: 'input-motivodesconsolidacion' }}
                                    size="small"
                                >
                                    <option key={0} value={0}>{"(Seleccione)"}</option>
                                    {listMotivos.map((row) => (
                                        <option key={row.id} value={row.id}>{row.descripcion}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <Alert severity={"info"}>
                                {"El pago vuelve a estar Activo y se puede anular o consolidar otra vez."}
                            </Alert>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button variant={"outlined"} onClick={cerrarDesconsolidar}>{"Cancelar"}</Button>
                    <Button
                        variant={"contained"}
                        startIcon={<IconUndo />}
                        disabled={idMotivoDesconsolidacion == 0}
                        onClick={confirmarDesconsolidar}
                    >
                        {"Desconsolidar"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Credenciales de quien aprueba la desconsolidacion. */}
            <DialogoCredenciales
                abierto={pidiendoCredenciales}
                accion={"desconsolidar"}
                acceso={"pago.desconsolidar"}
                titulo={"Aprobar Desconsolidacion"}
                mensaje={"Indique el usuario y la clave de quien tiene el acceso para desconsolidar."}
                onCancelar={() => setPidiendoCredenciales(false)}
                onConfirmar={(data) => {
                    setPidiendoCredenciales(false);
                    desconsolidar(data.token, data.usuario);
                }}
            />
        </div>
    )
}

export default ConsolidarPagos;